import classNames from 'classnames';
import { ReactNode, useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import logSignUpModalEmailTapped from '@alltrails/amplitude/events/logSignUpModalEmailTapped';
import { ApiError } from '@alltrails/core/utils/api';
import { post } from '@alltrails/core/utils/post';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import Logo from '@alltrails/denali/components/Logo';
import TextInput from '@alltrails/denali/components/TextInput';
import { ModalAriaLabelledByContext } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import isValidEmail from '../../utils/isValidEmail';
import TermsOfService from '../TermsOfService';
import styles from './EmailCheck.module.scss';
import sharedStyles from './SignUpModal.module.scss';

type OAuthProvider = 'Apple' | 'Google' | 'Facebook';

type EmailCheckProps = {
  heading?: ReactNode;
  onEmailAvailable: (email: string) => void;
  onEmailExists: (email: string) => void;
  onOAuthRequired: (email: string, provider: OAuthProvider) => void;
};

type EmailAvailableResponse = {
  available: boolean;
};

type EmailAvailableError = {
  code: string;
  message: string;
  target: 'Native' | 'Apple' | 'Google' | 'Facebook' | null;
  debug: null;
};

function validateEmail(value: string) {
  if (!isValidEmail(value)) {
    return <FormattedMessage defaultMessage="Email is not valid." />;
  }

  return undefined;
}

export default function EmailCheck({ heading, onEmailAvailable, onEmailExists, onOAuthRequired }: EmailCheckProps) {
  const ariaLabelledBy = useContext(ModalAriaLabelledByContext);
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<ReactNode>();
  const [serverError, setServerError] = useState<ReactNode>();
  const [submitting, setSubmitting] = useState(false);

  function handleChange(value: string) {
    setEmail(value);

    if (serverError) {
      setServerError(undefined);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    logSignUpModalEmailTapped();

    if (submitting) {
      return;
    }

    const validationError = validateEmail(email);
    if (validationError) {
      setEmailError(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const response = await post<EmailAvailableResponse>('/api/alltrails/emails/available', {
        email
      });

      if (response.available) {
        onEmailAvailable(email);
      } else {
        onEmailExists(email);
      }
    } catch (e: any) {
      if (e instanceof ApiError && e.data?.errors?.length > 0) {
        const error = e.data.errors[0] as EmailAvailableError;
        const errorCode = error.code;

        if (errorCode === 'registration_already_registered') {
          const target = error.target;

          if (target === 'Apple' || target === 'Google' || target === 'Facebook') {
            onOAuthRequired(email, target);
            return;
          }

          if (target === 'Native') {
            onEmailExists(email);
            return;
          }
        }

        if (errorCode === 'invalid_email') {
          setServerError(error.message);
          return;
        }

        onEmailAvailable(email);
      } else {
        setServerError(<FormattedMessage defaultMessage="Error, please try again" />);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={sharedStyles.content}>
      <div className={sharedStyles.logo}>
        <Logo variant="symbol" size="lg" color="monochrome-dark" />
      </div>
      <Heading as="h4" id={ariaLabelledBy} size="400" className={classNames(sharedStyles.header, sharedStyles.headingMaxWidth)}>
        {heading ?? <FormattedMessage defaultMessage="To keep exploring trails, log in or sign up for free" />}
      </Heading>
      <form className={styles.form} onSubmit={handleSubmit}>
        <TextInput
          autoFocus
          name="email"
          errorText={emailError}
          value={email}
          labelText={intl.formatMessage({ defaultMessage: 'Email address' })}
          onChange={handleChange}
          type="email"
        />
        {serverError && <div className={styles.serverError}>{serverError}</div>}
        <Button text={<FormattedMessage defaultMessage="Next" />} disabled={submitting} type="submit" variant="primary" width="full" />
      </form>
      <div className={styles.terms}>
        <TermsOfService />
      </div>
    </div>
  );
}
